import { useAuth } from "@/src/contexts/auth.context";
import { usePostCreationModal } from "@/src/hooks/features/posts/use-post-creation-modal";
import { Post } from "@/src/models/post.model";
import {
  ArrowDown3,
  AttachSquare,
  Camera,
  CloseCircle,
  Gallery,
  Global,
  Send,
  TagUser,
} from "iconsax-react-nativejs";
import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import {
  ActivityIndicator,
  Image,
  Modal,
  Pressable,
  ScrollView,
  Text,
  TextInput,
  View,
  Platform
} from "react-native";
import { useTheme } from "@/src/styles/theme-provider";
import { useTheme as customTheme } from "@/src/styles/theme-provider";
import ScButton from "../ui-kit/button";
import { FileAttachment } from "./post/file-attachment";
import VideoPreview from "./videos/video-preview";
import VisibilityModal from "./visibility-modal/post-visibility-modal";
import PostBodyInput from "./tag-input";
import ItemSelectorModal from "../action-modals/item-selection/item-selection-modal";
import { useGetUsers } from "@/src/services/api/groups/groups.hooks";
import { SelectableItem } from "../action-modals/item-selection/item-row";
import MentionedUserPill from "./mentioned-user-pill";

type CreatePostsProps = Readonly<{
  visible: boolean;
  insideGroup?: boolean;
  groupId?: string;
  onClose: () => void;
  onSubmit: (
    content: string,
    hashtags: string[],
    visibility: string,
    attachmentUrls: string[],
    visibleToGroupIds?: string[],
    visibleToUserIds?: string[],
  ) => void;
  post?: Post;
  isPosting?: boolean;
}>;

const CreatePostModal = ({
  visible,
  insideGroup,
  groupId,
  onClose,
  onSubmit,
  post,
  isPosting,
}: CreatePostsProps) => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const { themeVars, varStyle } = useTheme();
  const postCreationModal = usePostCreationModal(
    post,
    visible,
    onSubmit,
    onClose,
    isPosting,
    insideGroup,
    groupId,
  );

  const {
    data: usersData,
  } = useGetUsers();

  const users: SelectableItem[] = (
    usersData?.pages.flatMap((page) => page?.users ?? []) ?? []
  ).map((user) => ({
    itemId: user.itemId,
    displayName: user.displayName,
    userName: user.userName,
    profileImageUrl: user.profileImageUrl,
  }));

  return (
    <Modal visible={visible} animationType="fade" transparent>
      <View className="flex-1 justify-center items-center bg-black/40" style={themeVars as any}>
        <View className="bg-chalk rounded-2xl gap-4 w-[80%] md:w-[50%]">
          {/* Header */}
          <View className="flex-row justify-between items-start p-3 border-b border-ash-3">
            <View className="flex-row items-center gap-3">
              <Image
                source={{ uri: `${user?.profileImageUrl}` }}
                style={{ width: 40, height: 40 }}
                className="rounded-full"
              />
              <View className="gap-1">
                <Text className="font-medium text-sm text-onyx">{`${user?.firstName} ${user?.lastName}`}</Text>
                <Pressable
                  className="flex-row items-center gap-1 border border-ash-3 px-2 py-1 rounded-[8px] hover:bg-ash-3"
                  onPress={() => {
                    postCreationModal.setShowVisibilityModal(true);
                  }}
                >
                  <Global size={16} color={themeVars["--color-slate-default"] as string} />
                  <Text className="text-xs text-gray-dark">
                    {t(`LABEL_${(postCreationModal.visibility).toUpperCase()}`)}
                  </Text>
                  <ArrowDown3 size={16} color={themeVars["--color-slate-default"] as string} />
                </Pressable>
              </View>
            </View>
            <Pressable
              onPress={postCreationModal.handleClose}
              className="p-1.5 rounded-full hover:bg-ash-3"
            >
              <CloseCircle size={30} color={themeVars["--color-slate-default"] as string} />
            </Pressable>
          </View>
          <PostBodyInput
            value={postCreationModal.content}
            hashtags={postCreationModal.hashtags}
            onChange={postCreationModal.setContent}
            onTagsChange={postCreationModal.setHashtags}
          />
          {postCreationModal.mentionedUsers?.map((user) => (
            <View className="flex-row flex-wrap gap-2 px-3">
              <MentionedUserPill
                key={user.itemId}
                displayName={user.displayName ?? "Unknown"}
                onRemove={() => postCreationModal.setMentionedUsers((prev) => prev.filter((u) => u.itemId !== user.itemId))}
                userId={user.userId}
                isEditing={true}
              />
            </View>
          ))}

          {/* Media previews */}
          {postCreationModal.selectedMedia.length > 0 && (
            <View className="flex-row mt-2 px-3" style={themeVars as any}>
              <ScrollView horizontal showsHorizontalScrollIndicator={false}>
                {postCreationModal.selectedMedia.map((media, idx) =>
                  media.type?.startsWith("video/") ? (
                    <View key={idx} className="w-[100px] mr-2">
                      <VideoPreview
                        uri={media.uri}
                        onRemove={() =>
                          postCreationModal.handleRemoveMedia(idx)
                        }
                        closeButton={true}
                      />
                    </View>
                  ) : (
                    <View key={idx} className="mr-2">
                      <Image
                        source={{ uri: media.uri }}
                        style={{ width: 100, height: 100, borderRadius: 8 }}
                      />
                      <Pressable
                        onPress={() => postCreationModal.handleRemoveMedia(idx)}
                        style={{ backgroundColor: themeVars["--color-slate-default"] as string }}
                        className="absolute top-1 right-1 rounded-full hover:bg-ash-3"
                      >
                        <CloseCircle size={16} color="white" />
                      </Pressable>
                    </View>
                  ),
                )}
              </ScrollView>
            </View>
          )}

          {/* Attached files */}
          {postCreationModal.selectedFiles.length > 0 && (
            <ScrollView
              horizontal
              showsHorizontalScrollIndicator={false}
              className="px-4"
            >
              <View className="gap-2 flex-row">
                {postCreationModal.selectedFiles.map((file) => (
                  <FileAttachment
                    key={file.uri}
                    file={file}
                    onRemove={() =>
                      postCreationModal.handleRemoveFile(file.uri)
                    }
                  />
                ))}
              </View>
            </ScrollView>
          )}

          {/* Actions */}
          <View className="flex-row justify-between items-center py-2 px-3 border-t border-ash-3 gap-4 flex-wrap">
            <View className="flex-row gap-2">
              {Platform.OS !== "web" && (
                <Pressable
                  onPress={postCreationModal.handleCameraPress}
                  className="flex-row items-center hover:bg-ash-3 hover:rounded-full p-2"
                >
                  <Camera size={26} color={themeVars["--color-primary-default"] as string} />
                </Pressable>
              )}
              <Pressable
                onPress={postCreationModal.handlePickImage}
                className="flex-row items-center hover:bg-ash-3 hover:rounded-full p-2"
              >
                <Gallery size={26} color={themeVars["--color-primary-default"] as string} />
              </Pressable>
              <Pressable
                onPress={postCreationModal.handlePickFile}
                className="flex-row items-center hover:bg-ash-3 hover:rounded-full p-2"
              >
                <AttachSquare size={26} color={themeVars["--color-primary-default"] as string} />
              </Pressable>
              <Pressable
                onPress={postCreationModal.handleMentionUser}
                className="flex-row items-center hover:bg-ash-3 hover:rounded-full p-2"
              >
                <TagUser size={26} color={'var(--color-primary-default)'} />
              </Pressable>
            </View>

            <ScButton
              onPress={postCreationModal.handleSubmit}
              loading={postCreationModal.isLoading}
              disabled={isPosting || !postCreationModal.hasContent}
              style={{
                backgroundColor: postCreationModal.hasContent
                  ? themeVars["--color-primary-default"] as string
                  : themeVars["--color-ash-2"] as string,
              }}
              icon={
                isPosting ? (
                  <ActivityIndicator size={16} color="white" />
                ) : (
                  <Send color="white" />
                )
              }
              iconPosition="right"
              text={
                isPosting ? "BTN_POSTING" : post ? "BTN_UPDATE" : "BTN_POST"
              }
            />
          </View>
        </View>
      </View>

      <VisibilityModal
        visible={postCreationModal.showVisibilityModal}
        onClose={() => postCreationModal.setShowVisibilityModal(false)}
        selectedGroupIds={postCreationModal.selectedGroupIds}
        selectedUserIds={postCreationModal.selectedUserIds}
        setSelectedGroupIds={postCreationModal.setSelectedGroupIds}
        setSelectedUserIds={postCreationModal.setSelectedUserIds}
        toggleGroup={postCreationModal.toggleGroup}
        toggleUser={postCreationModal.toggleUser}
        visibility={postCreationModal.visibility}
        onVisibilityChange={postCreationModal.setVisibility}
      />

      <ItemSelectorModal
        items={users}
        visible={postCreationModal.showMentionModal}
        titleKey="LABEL_MENTION_USERS"
        confirmButtonKey="LABEL_ADD"
        onClose={() => { postCreationModal.setShowMentionModal(false) }}
        onConfirm={(ids, items) => { postCreationModal.setMentionedUsers(items) }}
      />
    </Modal>
  );
};

export default CreatePostModal;
